import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';

// TYPES
import { ComplaintsFormDataTypes } from './ComplaintsSubmit';
import {
	DomainComplaintCategory,
	ComplaintMemberCandidatesUserProfile,
	ComplaintCreateCommand
} from '../../api/models';

interface StepThreeTypes {
	complaintsFormData: ComplaintsFormDataTypes;
	setComplaintsFormData: React.Dispatch<React.SetStateAction<ComplaintsFormDataTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
	leaders: ComplaintMemberCandidatesUserProfile[];
	familyMembersData: ComplaintMemberCandidatesUserProfile[];
}
import { DomainComplaintLoggingType } from '../../api/models';

const StepThree: React.FC<StepThreeTypes> = ({
	complaintsFormData,
	setComplaintsFormData,
	setActiveStep,
	leaders,
	familyMembersData
}) => {
	const history = useHistory();

	// Get all Complaint Categories from Redux store
	const complaintCategories = useAppSelector((state) => state.lists.complaintCategories);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const {
		subject,
		loggingType,
		loggingOnBehalf,
		anonymousContact,
		category,
		occurrence,
		authority,
		location,
		accused,
		accusedRelationship,
		impacted,
		impactedRelationship,
		othersInvolved,
		witnesses,
		isInitialComplaint,
		description,
		comments,
		createAttachments,
		assignees,
		excludedLeaders,
		familyMembers,
		attachments
	} = complaintsFormData;

	const notifiedLeadershipTeamMembers = leaders.filter((item) => !excludedLeaders?.includes(item.id || ''));
	const notifiedFamilyMembers = familyMembersData.filter((item) => familyMembers?.includes(item.id || ''));

	const formData: ComplaintCreateCommand = {
		category,
		loggingType,
		loggingOnBehalf,
		anonymousContact,
		subject,
		occurrence,
		authority,
		location,
		accused,
		accusedRelationship,
		impacted,
		impactedRelationship,
		othersInvolved,
		witnesses,
		isInitialComplaint,
		description,
		comments,
		createAttachments,
		excludedLeaders,
		familyMembers
	};

	const submitComplaint = async () => {
		setIsLoading(true);

		const response = await apibridge.submitComplaint(formData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/complaints');

			useUpdateTotalNotification();
		}
	};

	const displayComplaintAuthor = (author?: string) => {
		if (author) {
			if (author === 'Myself') {
				return <>Myself</>;
			} else if (author === 'SomeoneElse' && loggingOnBehalf) {
				return <>{loggingOnBehalf}</>;
			} else if (author === 'Anonymous' && anonymousContact) {
				return <>Anonymously ({anonymousContact})</>;
			} else if (author === 'Anonymous' && !anonymousContact) {
				return <>Anonymously</>;
			} else return null;
		}
	};

	const displayCategory = (category?: DomainComplaintCategory) => {
		if (category) {
			const currentCategory = complaintCategories?.find((item) => item.value === category);
			return currentCategory?.label;
		}
	};

	return (
		<div>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<h2 className="general-header">Please review your complaint before submitting it</h2>

								<p className="sub-grey-text mbtom-48">
									The leadership team for this organisation will be notified of your complaint, other than those members
									you have chosen not to notify.
								</p>

								<div className="general-form-layout-wrapper">
									<div className="complaints-detail">
										<div className="detail-block">
											<h2 className="detail-heading">
												Complaint logged{' '}
												{loggingType === DomainComplaintLoggingType.SomeoneElse && loggingOnBehalf
													? 'on behalf of'
													: 'by'}{' '}
											</h2>
											<p className="detail-info">{displayComplaintAuthor(loggingType)}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Complaint subject</h2>
											<p className="detail-info">{subject}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Complaint Type</h2>
											<p className="detail-info">{displayCategory(category)}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Date and time of relevant event</h2>
											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Location of relevant event</h2>
											<p className="detail-info">{location}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who is the complaint about?</h2>
											<p className="detail-info">{accused}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What is your relationship to this person(s)?</h2>
											<p className="detail-info">{accusedRelationship}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Name of person(s) impacted</h2>
											<p className="detail-info">{impacted}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What is your relationship to this person(s)?</h2>
											<p className="detail-info">{impactedRelationship}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who else was involved in the relevant event?</h2>
											<p className="detail-info">{othersInvolved}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who witnessed the relevant event?</h2>
											<p className="detail-info">{witnesses}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Is this your first complaint about this matter?</h2>
											<p className="detail-info">{isInitialComplaint ? 'Yes' : 'No'}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">
												Please detail your complaint including details of relevant event/s.
											</h2>
											<p className="detail-info">{description}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">
												Please provide the name of the authority and the contact details of the person that you spoke to
												at that authority
											</h2>
											<p className="detail-info">{authority}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Additional comments</h2>
											<p className="detail-info">{comments}</p>
										</div>

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h2 className="detail-heading">Attachments</h2>
												<ul>
													{attachments.map((item, index) => {
														return (
															<li key={index}>
																<div className="attachments-download-link">
																	<span className="link-name">{item.displayFileName}</span>
																</div>
															</li>
														);
													})}
												</ul>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading mbtom-14">Complaint notified to:</h2>

											<h3 className="detail-heading mbtom-14">Leadership Team members</h3>
											<ul className="mbtom-14">
												{notifiedLeadershipTeamMembers &&
													notifiedLeadershipTeamMembers.length > 0 &&
													notifiedLeadershipTeamMembers.map((item, index) => {
														return (
															<li key={index} className="row mbtom-14 w-100">
																<div className="col-2">
																	<CircleImage fullName={item.name} src={item.profileImage} size={40} />
																</div>

																<div className="col-5 people-name">{item.name}</div>

																<div className="col-5 people-role">{item.role}</div>
															</li>
														);
													})}
											</ul>

											{notifiedFamilyMembers && notifiedFamilyMembers.length > 0 && (
												<>
													<h3 className="detail-heading mbtom-14">Family members</h3>

													<ul>
														{notifiedFamilyMembers.map((item, index) => {
															return (
																<li key={index} className="row mbtom-14 w-100">
																	<div className="col-2">
																		<CircleImage fullName={item.name} src={item.profileImage} size={40} />
																	</div>

																	<div className="col-5 people-name">{item.name}</div>

																	<div className="col-5 people-role">{item.role}</div>
																</li>
															);
														})}
													</ul>
												</>
											)}
										</div>
									</div>

									<div className="general-form-guide">
										<QuestionMarkIcon />

										<h2 className="guide-heading">When to submit a complaint</h2>

										<p className="guide-content">
											Complaints are the most serious way for individuals to voice their safety concerns. Complaints
											about children’s safety must be taken seriously, investigated thoroughly, and actioned
											appropriately. A ChildAware organisation will have a comprehensive, child-focused complaints
											handling policy available to staff, parents, children, volunteers and visitors that will guide how
											complaints are to be handled. ChildAware organisations recognise that complaints present an
											opportunity for complainants to have their concerns addressed and for organisations to continually
											improve child safety practices.
										</p>

										<p className="guide-content">
											If you have a general suggestion or comment about how an organisation might improve children’s
											safety you may wish to submit&nbsp;
											<Link className="link" to="/feedback">
												Feedback
											</Link>
											&nbsp;instead.
										</p>

										<p className="guide-content">
											<strong>Note:</strong> If a child is in immediate danger, call police or emergency services on
											000.
										</p>
									</div>
								</div>

								<div className="general-divider"></div>

								<div className="form-two-button-group">
									<Button
										buttonIcon={<ArrowLeftIcon className="link-icon" />}
										className="btn-secondary edit-btn"
										onClick={() => {
											setActiveStep('step1');

											window.scrollTo(0, 0);
										}}
									>
										Edit details
									</Button>

									{isOrganisationActive && (
										<Button className="btn-primary submit-btn" onClick={submitComplaint}>
											Submit complaint
										</Button>
									)}
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default StepThree;
